import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ContactListItem } from "@multi-chat/shared";
import { Search } from "lucide-react";
import { LtrValue } from "../../components/common/LtrValue.js";
import { cn } from "../../lib/utils.js";
import { LinkedPlatformBadges } from "./LinkedPlatformBadges.js";
import { useContactSearch } from "./use-contact-search.js";

export function ContactSearchPicker({
  organizationId,
  excludeContactId,
  selectedContactId,
  onSelect,
  disabled,
  searchLabel,
  searchPlaceholder,
  emptyLabel,
}: {
  organizationId: number;
  excludeContactId?: number;
  selectedContactId: number | null;
  onSelect: (contact: ContactListItem) => void;
  disabled?: boolean;
  searchLabel: string;
  searchPlaceholder: string;
  emptyLabel?: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const { state } = useContactSearch(organizationId, search, excludeContactId);
  const searching = state.kind === "searching";
  const searchError = state.kind === "error";
  const results = state.kind === "ready" ? state.results : [];
  const queryReady = state.kind !== "idle";

  return (
    <div className="grid gap-3" data-testid="contact-search-picker">
      <label className="block space-y-1.5">
        <span className="text-[0.8125rem] font-medium">{searchLabel}</span>
        <div className="relative">
          <Search
            size={14}
            className="pointer-events-none absolute start-2.5 top-1/2 -translate-y-1/2 text-muted"
            aria-hidden="true"
          />
          <input
            className="w-full rounded-md border border-border bg-background py-2 ps-8 pe-3 text-[0.8125rem]"
            value={search}
            placeholder={searchPlaceholder}
            disabled={disabled}
            onChange={(event) => setSearch(event.target.value)}
            data-testid="contact-search-picker-input"
          />
        </div>
      </label>

      {searching ? (
        <p className="m-0 text-[0.8125rem] text-muted">{t("contacts.search.searching")}</p>
      ) : null}

      {searchError ? (
        <p className="m-0 text-[0.8125rem] text-red-600">{t("contacts.search.failed")}</p>
      ) : null}

      {results.length > 0 ? (
        <ul
          className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-border p-1"
          data-testid="contact-search-picker-results"
        >
          {results.map((contact) => {
            const selected = selectedContactId === Number(contact.id);
            return (
              <li key={contact.id}>
                <button
                  type="button"
                  data-testid={`contact-search-result-${contact.id}`}
                  className={cn(
                    "flex w-full min-w-0 items-center gap-2 rounded-md px-2 py-1.5 text-start text-[0.8125rem] hover:bg-surface-muted",
                    selected ? "bg-accent/8 ring-1 ring-accent/20" : null,
                  )}
                  onClick={() => onSelect(contact)}
                >
                  <span className="grid min-w-0 flex-1 gap-0.5">
                    <span className="truncate font-medium">{contact.displayName}</span>
                    <span className="flex min-w-0 flex-wrap gap-x-2 text-[0.7rem] text-muted">
                      {contact.normalizedPhoneNumber ? (
                        <LtrValue className="truncate tabular-nums" title={contact.normalizedPhoneNumber}>
                          {contact.normalizedPhoneNumber}
                        </LtrValue>
                      ) : null}
                      {contact.email ? (
                        <LtrValue className="truncate" title={contact.email}>
                          {contact.email}
                        </LtrValue>
                      ) : null}
                    </span>
                  </span>
                  <LinkedPlatformBadges types={contact.linkedPlatformTypes} />
                </button>
              </li>
            );
          })}
        </ul>
      ) : queryReady && !searching && !searchError ? (
        <p className="m-0 text-[0.8125rem] text-muted">
          {emptyLabel ?? t("contacts.search.noResults")}
        </p>
      ) : null}
    </div>
  );
}
